(function(angular) {
    'use strict';

    function CachedUser(User, CacheExpirationDateISOString) {
        this.User = User;
        this.CacheExpirationDateISOString = CacheExpirationDateISOString;
    }

    angular.module('GorillaBoy.services')
    .factory('UserCollectionService', ['$http', 'EndpointService', 'AppConstants', '$q', 'localStorageService', UserCollectionServiceFactory]);

    function UserCollectionServiceFactory($http, EndpointService, AppConstants, $q, localStorageService) {

        //AppConstant bindings
        var httpMethods = AppConstants.HttpMethods;
        var thisService = {},
            USER_INFO_EXPIRY_IN_MILLI_SEC = AppConstants.USER_INFO_EXPIRY_IN_HRS * 3600 * 1000,
            SERVICE_ROOT = function () {
                return EndpointService.endpoint() + 'collection/user';
            };

        //delete old key and its data
        localStorageService.remove('user_info');


        /**
         * This method will delete any obsolete user data in the local storage once per app load
         * This is to make sure any stale data does not grow in the local storage
         */
        var allUserDataStringified = localStorageService.get(AppConstants.USER_INFO_LOCAL_STORAGE_KEY),
            now = Date.now(),
            allUserData = [];

        if (allUserDataStringified) {
            allUserData = (allUserDataStringified);
        }
        allUserData = allUserData.filter(function(cachedUser) {
            //Get only users who's cache expiration date is in the future
            return (new Date(cachedUser.CacheExpirationDateISOString)) > now;
        });
        localStorageService.set(AppConstants.USER_INFO_LOCAL_STORAGE_KEY, allUserData);

        /**
         * This method's sole responsibility is to read from Local Storage API
         * It safely constructs the cached user list
         *
         * @return Array<CachedUser> List of cached user object wrappers
         */
        function readUserCacheFromStorage() {
            var cachedUserCollectionStringified = localStorageService.get(AppConstants.USER_INFO_LOCAL_STORAGE_KEY),
                cachedUserCollection = [];

            if (cachedUserCollectionStringified) {
                cachedUserCollection = (cachedUserCollectionStringified);
            }
            return cachedUserCollection;
        }

        /**
         * This method takes Users object and persists it to local storage by converting them to CachedUser with expiration time
         *
         * @param Users is an object with IDs as they keys and User model as the values
         * @return Array<User> An array of user objects
         */
        function writeUserCacheToStorage(Users) {
            var ExpiryTimeISOString = (new Date(Date.now() + USER_INFO_EXPIRY_IN_MILLI_SEC)).toISOString();
            var cachedUserList = [],
                Ids = [];
            angular.forEach(Users, function(User, Id) {
                cachedUserList.push(new CachedUser(User, ExpiryTimeISOString));
                Ids.push(Id);
            });

            var cachedUserCollection = readUserCacheFromStorage();
            cachedUserCollection = cachedUserCollection.filter(function(cachedUser) {
                var userId = cachedUser.User.Id;
                return Ids.indexOf(userId) === -1;
            });

            localStorageService.set(AppConstants.USER_INFO_LOCAL_STORAGE_KEY, cachedUserList.concat(cachedUserCollection));
        }

        /**
         * This method will be responsible for the actual filtering logic
         *
         * @param cachedUserCollection List of CachedUser objects containing user and their cache expiration time
         * @return Array<User> list of User objects from cachedUserCollection with IDs present in userIds array
         */
        function filterCachedUsers(cachedUserCollection, userIds) {
            var now = Date.now();
            return cachedUserCollection.filter(function(cachedUser) {
                var userId = cachedUser.User.Id;
                //Get only users who's cache expiration date is in the future
                return (new Date(cachedUser.CacheExpirationDateISOString)) > now && userIds.indexOf(userId) !== -1;
            }).map(function(cachedUser) {
                return cachedUser.User;
            });
        }

        thisService.__getUserCollectionPendingPromise = null;

        /**
         * For the given user IDs, fetch the user details from local storage or Galaxy service.
         * @param userIDs User IDs
         */
        thisService.getUserCollection = function(userIds) {
            userIds = userIds.sort();
            userIds = _.uniq(userIds, true); //Passing isSorted flag as true will use faster algorithm

            return $q(function(resolve, reject) {
                if (thisService.__getUserCollectionPendingPromise) {
                    //If there's already a pending promise, then wait until it's resolved!
                    var fnCallback = getUserCollectionFn.bind(thisService, resolve, reject);
                    thisService.__getUserCollectionPendingPromise = thisService.__getUserCollectionPendingPromise.then(fnCallback, fnCallback);
                } else {
                    getUserCollectionFn(resolve, reject);
                }
            });


            function getUserCollectionFn(resolve, reject) {
                var cachedUserCollection = readUserCacheFromStorage();
                var userCollection = filterCachedUsers(cachedUserCollection, userIds);
                var existingUserIds = userCollection.map(function(User) {
                    return User.Id;
                });
                var pendingUserIds = _.difference(userIds, existingUserIds);
                var allUserIds = userIds.concat(existingUserIds);

                //Make API call is there is more data to fetch
                if (pendingUserIds.length) {
                    thisService.__getUserCollectionPendingPromise = thisService.getUserCollectionFromServer(pendingUserIds)
                    if(thisService.__getUserCollectionPendingPromise !== undefined){
                        thisService.__getUserCollectionPendingPromise
                        .then(function(response) {
                            var Users = response.data.Users,
                                result = [];

                            //Reset the value to NULL, so that subsequent callers don't block.
                            thisService.__getUserCollectionPendingPromise = null;

                            angular.forEach(Users, function(User) {
                                //Do NOT store email addresses in local storage
                                delete User.Username;
                                delete User.EmailAddress;

                                result.push(User);
                            });
                            writeUserCacheToStorage(Users);
                            //Make sure you write to Local Storage before resolving this promise
                            resolve(result.concat(userCollection));
                        }, function(response) {

                            //Reset the value to NULL, so that subsequent callers don't block.
                            thisService.__getUserCollectionPendingPromise = null;

                            //We still resolve with userCollection (possibly stale) data
                            resolve(userCollection);
                        });
                    }
                } else {
                    resolve(userCollection);
                }
            }
        };

        /**
         * Talks to the WorkDocs API to fetch the User objects for the list of userIds passed.
         * @param userIds Array<String> containing all the user IDs
         * @return Promise which will resolve to Map<String, User> object or @return undefined if there is no userIds 
         */
        thisService.getUserCollectionFromServer = function(userIds) {
            userIds = userIds.filter(function(id) { 
                return id !== AppConstants.APP_LOCK_IDS.HANCOM && id !== AppConstants.APP_LOCK_IDS.WOPI; 
            });
            if(userIds.length) {
                var config = {
                    url: SERVICE_ROOT(),
                    method: httpMethods.POST,
                    data: {
                        UserIds: userIds
                    }
                };
                return $http(EndpointService.tokenize(config));
            } 
        };

        return thisService;
    }
}(angular));
